/**
 * Location rename (Phase 3): the API may answer with locationGuid/locationDocId (dual-emitted
 * from Task 7; new-only on non-device routes once the Task 26 flag is on). In-repo code reads the
 * LEGACY property names (TS data-shape properties are never renamed — plan spec gap 1), so every
 * ingress of an API response folds the new names back: the legacy name takes the new value (new
 * wins) and the new key is DELETED, so it can never be spread into a stored document. `null` is
 * preserved; non-plain values are untouched. Ported to apps/functions/src/utils/locationWireFold.ts
 * and tested against the same cases file.
 */
const PAIRS: ReadonlyArray<readonly [legacy: string, next: string]> = [
  ['restaurantGuid', 'locationGuid'], // restaurant-ok: wire field names
  ['restaurantDocId', 'locationDocId'], // restaurant-ok: wire field names
];

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

export function foldLocationResponseNames<T>(value: T, depth = 0): T {
  if (depth > 32) return value;
  if (Array.isArray(value)) { for (const v of value) foldLocationResponseNames(v, depth + 1); return value; }
  if (!isPlainObject(value)) return value;
  // A non-generic view of the same object: TS 6 refuses writes through a
  // generic-indexed `T & Record<…>` (TS2862). Same object, same behaviour.
  const record: Record<string, unknown> = value;
  for (const [legacy, next] of PAIRS) {
    if (Object.prototype.hasOwnProperty.call(record, next)) {
      record[legacy] = record[next];
      delete record[next];
    }
  }
  for (const v of Object.values(record)) foldLocationResponseNames(v, depth + 1);
  return value;
}

/** A wire key's name after the fold: each new name becomes its legacy name, as PAIRS says. */
type FoldedKey<K> = K extends 'locationGuid' // restaurant-ok: wire field names
  ? 'restaurantGuid' // restaurant-ok: wire field names
  : K extends 'locationDocId' // restaurant-ok: wire field names
    ? 'restaurantDocId' // restaurant-ok: wire field names
    : K;

/**
 * The type twin of {@link foldLocationResponseNames}: what an API response of type `T` is once
 * the web's ingress has folded it. `locationGuid` reads as `restaurantGuid` and `locationDocId`
 * as `restaurantDocId`, through nested objects and arrays; the new keys are gone. A service whose
 * response goes through `createApiClient` returns `LocationWireFolded<View>`, so a caller reads the
 * key that actually arrives (a `View` type would promise a `locationGuid` that is always
 * undefined). Functions and Dates are not plain objects and are left as they are, as the fold
 * leaves them.
 */
export type LocationWireFolded<T> = T extends (...args: never[]) => unknown
  ? T
  : T extends Date
    ? T
    : T extends readonly unknown[]
      ? { [I in keyof T]: LocationWireFolded<T[I]> }
      : T extends object
        ? { [K in keyof T as FoldedKey<K>]: LocationWireFolded<T[K]> }
        : T;
